/**
 * Shared consent form clauses — each is either a static string or a
 * function that accepts the patient name.
 */

export type Clause = string | ((patientName: string) => string)

/** Resolve a clause to its final text. */
export function resolveClause(clause: Clause, patientName: string): string {
  return typeof clause === 'function' ? clause(patientName) : clause
}

export const PERMISSION_CLAUSE =
  'I hereby give permission for the administration of an anaesthetic to the above animal and to the surgical or other procedures detailed on this form together with any other procedures which may prove necessary.'

export const NATURE_EXPLAINED_CLAUSE =
  'The nature of these procedures and of other such procedures as might prove necessary has been explained to me.'

export const ANAESTHESIA_RISK_CLAUSE =
  'I understand that there are some risks involved in all anaesthetic techniques and surgical procedures. I have been advised that anaesthesia always involves risk. Rare but possible complications include allergic reactions, respiratory distress, cardiovascular issues & death'

export const EXTRACTION_RISK_CLAUSE =
  'I have been advised that dental extraction always involves risk. Rare but possible completions include wound breakdown, infection & jaw fracture.'

export const EMERGENCY_AUTH_CLAUSE: Clause = (patientName) =>
  `In the rare event of an emergency, I authorise the vets and staff of Snoots Vet to provide the necessary critical care and resuscitation for ${patientName}. I understand this may incur additional costs.`

export const COST_ACCEPTANCE_CLAUSE =
  'I accept that the likely cost will be as detailed on the estimate and that in the event of further treatment being required or of complications occurring which will give rise to additional costs, I shall be contacted as soon as practicable so that my consent to such additional treatment and costs may be obtained.'

export const VET_BEST_INTEREST_CLAUSE =
  'In the event that the veterinary surgeon is unable to contact me on the numbers provided, I understand the veterinary surgeon will act in the best interests of my animal.'

export const OFF_LABEL_MEDICINES_CLAUSE =
  'In order to protect the welfare of my animal, in the unlikely event of an emergency, or where additional pain relief or sedation may be required, I understand the veterinary surgeon may decide to use medicines that are not authorised for use in pets.'

export const PRE_ANAESTHETIC_BLOOD_CLAUSE =
  "For pets under the age of 7 years, pre-anaesthetic blood testing is optional. This test helps to assess your pet's overall health and detect any underlying issues, such as liver or kidney abnormalities, that may affect the administration of general anaesthesia. If you choose to opt-in for this testing, an additional charge of £43 will be applied directly to your card."

export const IV_FLUIDS_CLAUSE = 'Intravenous fluids are included as needed.'

export const DISCHARGE_TIME_CLAUSE =
  'I understand that Snoots is a high volume clinic, and a discharge time cannot be guaranteed. Discharge can be as late as 7pm.'

export const DENTAL_S1_EXTENT_CLAUSE =
  "I understand that the extent of dental disease cannot be fully determined before the teeth are examined under general anaesthetic. I understand that should extractions be required this will be carried out in a separate procedure to be scheduled after this one. The vet will review my pets' X-rays & discuss the findings with me on the Monday following the procedure."

export const DENTAL_S2_FINDINGS_CLAUSE: Clause = (patientName) =>
  `I understand that ${patientName} will be having extractions today based upon the findings from Stage 1 of their procedure.`

export const DENTAL_S2_COST_CLAUSE =
  'I understand that this cost of these extractions are £399 for less than 5 teeth removed and £599 for more than 5 teeth removed.'

export const DENTAL_S2_MORE_EXTRACTIONS_CLAUSE =
  'Should more extractions be required than initially thought, I understand that the vet will call me to discuss this prior to removing the additional teeth.'

export const FEEDING_CONSENT_TEXT: Clause = (patientName) =>
  `Are you happy for us to feed ${patientName} after the procedure? We feed Hills I/D, a light food ideal for post general anaesthetic.`
